import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { routes } from '#apps/client-web/app/routes';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type AuditId } from '#packages/data-collection/models/audit';
import { type AuditLightingCountForm } from '#packages/data-collection/models/audit-lighting-count-form';
import { type AuditLightingTypeForm } from '#packages/data-collection/models/audit-lighting-type-form';

export type AuditFormLightingCountPageViewModel = ReturnType<
  typeof useAuditFormLightingCountPageViewModel
>;

type AuditFormLightingCountPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  queryClient: QueryClient;
};

export const useAuditFormLightingCountPageViewModel = ({
  logger,
  auditFormService,
  queryClient,
}: AuditFormLightingCountPageViewModelProps) => {
  const { auditId } = useParams<{ auditId: AuditId }>();
  const navigate = useNavigate();

  const auditFormQuery = useLoadAuditFormByType<AuditLightingCountForm>({
    auditId: auditId!,
    queryClient,
    formType: 'lighting-count',
    auditFormService,
  });

  const lightingFormQuery = useLoadAuditFormByType<AuditLightingTypeForm>({
    auditId: auditId!,
    queryClient,
    formType: 'lighting-type',
    auditFormService,
  });

  const onCreate = useCallback(() => {
    logger?.info('Navigating to create lighting count form entry page');
    navigate(
      routes.audit.getEnergyAuditLightingCountFormEntryPath(auditId!, -1),
    );
  }, [navigate, auditId]);

  return {
    auditId,
    auditFormQuery,
    lightingFormQuery,
    onCreate,
  };
};
